import React, { useCallback, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import px from 'prop-types';
import { v4 } from 'uuid';
import { useViewport, useTranslation } from 'Common/hooks';
import { Product, Facet } from 'Common/types';
import { Translation } from 'Common/components/localization';
import { ImageModal, NumberInput } from 'Common/components/ui';
import { Grid, Header, Row, Cell } from 'Common/components/ui/Grid';
import ProductBadge from './ProductBadge';
import ProductPrice from './ProductPrice';
import { ProductVariant } from 'Common/models';
import { getSpecificationColumns } from 'Common/utils/ProductSpecifications';

function buildColsFromAttributes(variants = []) {
    return getSpecificationColumns(variants).map(({ Attribute, AttributeDisplayName }) => [
        Attribute,
        AttributeDisplayName,
        true,
    ]);
}

export default function ProductListTable({
    facets,
    getName = (x) => x.Code,
    id: idProp = `PLT_${v4()}`,
    NotifyMe,
    notifyProps,
    onQuantityChange,
    onRowClick,
    quantities = {},
    showVariantImages = false,
    useSpecificationColumns = false,
    variants = [],
    useCompare = false,
    comparedItems = [],
    onToggleCompare,
}) {
    const viewport = useViewport();
    const isMobile = useMemo(() => viewport.is.lt('md'), [viewport]);
    const [showModal, setShowModal] = useState(false);
    const [showModalCarousel, setShowModalCarousel] = useState(false);
    const [modalContent, setModalContent] = useState({});
    const [variantMedia, setVariantMedia] = useState([]);
    const shouldShowNotifyColumn = variants.some((v) => v.StockStatus === 'OutOfStock');
    const shouldShowQuantityColumn = Boolean(onQuantityChange) && variants.some((v) => v.StockStatus === 'InStock');
    const shouldShowPurchaseAction = shouldShowNotifyColumn || shouldShowQuantityColumn;

    const tooltip = useTranslation('Commerce.Product.Varient.Images.ToolTip');
    const compareLabel = useTranslation('Commerce.Product.Compare.Add.Label');
    const comparedLabel = useTranslation('Commerce.Product.Compare.Added.Label');
    const id = useRef(idProp);
    const key = useCallback((...args) => [id.current, ...args].join('__'), []);
    const cols = useMemo(
        () =>
            useSpecificationColumns
                ? buildColsFromAttributes(variants)
                : (facets?.map((f) => [f.Attribute, f.AttributeDisplayName || f.DisplayName]) ??
                  buildColsFromAttributes(variants)),
        [facets, useSpecificationColumns, variants]
    );

    const closeModal = useCallback(() => setShowModal(false), []);

    const setShowModalSlider = useCallback(() => setShowModalCarousel(true), []);

    const onClickView = useCallback(
        (vM) => {
            setVariantMedia(vM);
            setModalContent({
                type: vM[0].FileType,
                src: vM[0].Url,
                isMobile: isMobile,
                selectedItem: vM[0],
            });
            setShowModal(true);
        },
        [isMobile]
    );

    return (
        <>
            <Grid className="ProductListTable">
                <Header>
                    <Cell
                        sticky="left"
                        className={cx('d-flex px-4', `justify-content-${showVariantImages ? 'between' : 'start'}`)}
                    >
                        <div className="ps-3 pe-3">
                            <Translation id="Commerce.Product.Tabs.TypeId.Label" />
                        </div>
                        {showVariantImages ? (
                            <div>
                                <Translation id="Commerce.Product.Tabs.Images.Label" />
                            </div>
                        ) : null}
                    </Cell>
                    {!cols.length ? (
                        <Cell>&nbsp;</Cell>
                    ) : (
                        cols.map(([c, d]) => (
                            <Cell key={key('body', 'col', c)} className={cx('d-flex justify-content-center ps-3 pe-3')}>
                                {d || c}
                            </Cell>
                        ))
                    )}
                    <Cell
                        className="ProductListTable__actionHeader ps-3 pe-0"
                        sticky="right"
                        width="minmax(180px, max-content)"
                    >
                        {shouldShowPurchaseAction ? (
                            <div className="ProductListTable__priceAndPurchaseRow">
                                <div className="ProductListTable__priceRow">
                                    <Translation id="Commerce.Product.Tabs.Price.Label" />
                                </div>
                                <div
                                    className="ProductListTable__purchaseRow ProductListTable__purchaseRow--header"
                                    aria-hidden="true"
                                />
                            </div>
                        ) : (
                            <div className="ProductListTable__priceRow">
                                <Translation id="Commerce.Product.Tabs.Price.Label" />
                            </div>
                        )}
                    </Cell>
                </Header>
                {variants?.map((v) => (
                    <Row key={key('head', 'name', v.Code)}>
                        <Cell
                            onClick={onRowClick}
                            className={cx(showVariantImages ? '' : 'd-flex justify-content-start align-items-center')}
                            sticky="left"
                        >
                            <div
                                className={cx(
                                    `justify-content-${showVariantImages ? 'between header-column' : 'start'}`,
                                    'd-flex ps-4'
                                )}
                            >
                                <div
                                    className={cx(
                                        'd-flex flex-column align-items-start justify-content-end',
                                        showVariantImages ? 'me-4' : ''
                                    )}
                                >
                                    <ProductBadge product={v} />
                                    <div>{getName(v)}</div>
                                </div>
                                {showVariantImages ? (
                                    <button
                                        className="btn btn-primary btn-sm gallery-btn"
                                        onClick={() => onClickView(v.GalleryMedia)}
                                        title={tooltip}
                                        disabled={!v.GalleryMedia.length || v.GalleryMedia[0].ContentId == 0}
                                    >
                                        <i className="far fa-images" />
                                    </button>
                                ) : null}
                            </div>
                        </Cell>
                        {!cols.length ? (
                            <Cell />
                        ) : (
                            cols.map(([c, d, isSpecification]) => (
                                <Cell
                                    key={key('body', 'cell', c, v.Code)}
                                    className={cx('d-flex align-items-center justify-content-center px-3')}
                                >
                                    {v.Attributes.find((a) =>
                                        isSpecification
                                            ? a.IsSpecification && a.AttributeDisplayName === d
                                            : a.AttributeName === c
                                    )?.Values?.join(', ') ?? ''}
                                </Cell>
                            ))
                        )}
                        <Cell className="ProductListTable__actionCell ps-3" sticky="right">
                            <div className="ProductListTable__actionStack">
                                <div className="ProductListTable__priceAndPurchaseRow">
                                    <div className="ProductListTable__priceRow">
                                        <ProductPrice
                                            priceInfo={{
                                                price: v.Price,
                                                useQtySalePrice:
                                                    v.Price.QtySalePrices != null &&
                                                    Object.keys(v.Price.QtySalePrices).length > 0,
                                            }}
                                            quantity={quantities[v.Code] ?? 0}
                                        />
                                    </div>

                                    {shouldShowPurchaseAction ? (
                                        <div className="ProductListTable__purchaseRow">
                                            {ProductVariant.isDisplayOnly(v) ? null : v.StockStatus === 'InStock' ? (
                                                <div className="num-input">
                                                    <NumberInput
                                                        min={0}
                                                        step={1}
                                                        noDecimal
                                                        value={quantities[v.Code] ?? 0}
                                                        onChange={(n) => onQuantityChange(n, v.Code)}
                                                        useStepBtns
                                                    />
                                                </div>
                                            ) : (
                                                <NotifyMe {...notifyProps} code={v.Code} />
                                            )}
                                        </div>
                                    ) : null}
                                </div>

                                {useCompare ? (
                                    <div className="ProductListTable__compareRow">
                                        <button
                                            type="button"
                                            className={cx(
                                                'ProductListTable__compareBtn btn btn-link btn-sm',
                                                comparedItems.includes(v.Code) && 'is-active'
                                            )}
                                            onClick={() => onToggleCompare(v.Code)}
                                            aria-pressed={comparedItems.includes(v.Code)}
                                            aria-label={`Compare ${getName(v)}`}
                                        >
                                            <i className="fa fa-chart-bar"></i>
                                            {comparedItems.includes(v.Code) ? comparedLabel : compareLabel}
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                        </Cell>
                    </Row>
                ))}
            </Grid>
            <div className="ProductGallery">
                <ImageModal
                    className="ProductGallery__Modal"
                    closeModal={closeModal}
                    isMobile={isMobile}
                    items={variantMedia}
                    modalContent={modalContent}
                    modalSize="xl"
                    setModalContent={setModalContent}
                    setShowModalSlider={setShowModalSlider}
                    showModal={showModal}
                    showCarousel={showModalCarousel}
                />
            </div>
        </>
    );
}

ProductListTable.propTypes = {
    facets: px.arrayOf(Facet),
    getName: px.func,
    id: px.string,
    isPlp: px.bool,
    NotifyMe: px.elementType,
    notifyProps: px.objectOf(px.any),
    onQuantityChange: px.func,
    onRowClick: px.func,
    quantities: px.objectOf(px.number),
    showVariantImages: px.bool,
    useSpecificationColumns: px.bool,
    variants: px.arrayOf(Product),
    useCompare: px.bool,
    comparedItems: px.arrayOf(px.string),
    onToggleCompare: px.func,
};
